// composable/useSocketManager.js

import Cookies from 'js-cookie';
import { io } from 'socket.io-client';
import { ref, computed } from 'vue';

// Module-level singleton
const socketInstance = ref(null);
const isConnected = ref(false);
const isConnecting = ref(false);
const connectionAttempts = ref(0);
const pendingActions = ref([]);
const isInitialized = ref(false);

const MAX_RETRY_ATTEMPTS = 10;
const RETRY_DELAYS = [500, 1000, 2000, 3000, 5000];

export function useSocketManager() {
    
    function connectServer(serverURL, namespace, query) {
        return new Promise((resolve, reject) => {
            try {
                let socket = io(`${serverURL}/${namespace}`, {
                    query,
                    auth: { token: Cookies.get('accessToken') },
                    autoConnect: true,
                    reconnection: true,
                    reconnectionAttempts: Infinity
                });
                socket.on('connect_error', (error) => {
                    reject(error);
                });
                socket.on('connect', () => {
                    resolve(socket);
                });
            } catch (error) {
                console.error(error);
                reject(error);
            }
        });
    }

    function initSocket(socket) {
        if (!socket) return;
        
        // Prevent double init
        if (isInitialized.value && socketInstance.value === socket) {
            return;
        }

        socketInstance.value = socket;
        isInitialized.value = true;

        // Setup connection listeners
        socket.off('connect', handleConnect);
        socket.off('disconnect', handleDisconnect);
        socket.off('connect_error', handleConnectError);
        socket.off('reconnect', handleReconnect);
        socket.off('reconnect_attempt', handleReconnectAttempt);
        
        socket.on('connect', handleConnect);
        socket.on('disconnect', handleDisconnect);
        socket.on('connect_error', handleConnectError);
        socket.on('reconnect', handleReconnect);
        socket.on('reconnect_attempt', handleReconnectAttempt);

        // Check initial state
        if (socket.connected) {
            handleConnect();
        }  
    }

    function handleConnect() {
        isConnected.value = true;
        isConnecting.value = false;
        connectionAttempts.value = 0;
        processPendingActions();
    }

    function handleDisconnect(reason) {
        isConnected.value = false;

        if (reason === 'io server disconnect') {
            socketInstance.value?.connect();
        }
    }

    function handleConnectError(error) {
        console.error('[SocketManager] Connection error:', error);
        isConnecting.value = false;

        if (connectionAttempts.value < MAX_RETRY_ATTEMPTS) {
            const delay = RETRY_DELAYS[Math.min(connectionAttempts.value, RETRY_DELAYS.length - 1)];

            setTimeout(() => {
                connectionAttempts.value++;
                socketInstance.value?.connect();
            }, delay);
        }
    }

    function handleReconnect(attemptNumber) {
        console.log('[SocketManager] Reconnected after', attemptNumber, 'attempts');
        isConnected.value = true;
        connectionAttempts.value = 0;
        processPendingActions();
    }

    function handleReconnectAttempt(attemptNumber) {
        console.log('[SocketManager] Reconnect attempt:', attemptNumber);
        isConnecting.value = true;
    }

    /**
     * Wait for socket to be initialized AND connected
     */
    function waitForConnection(timeout = 10000) {
        return new Promise((resolve) => {
            const startTime = Date.now();
            const checkInterval = 100;

            const checkConnection = setInterval(() => {
                // Check cả initialized và connected
                if (isInitialized.value && socketInstance.value?.connected && socketInstance.value?.id) {
                    clearInterval(checkConnection);
                    isConnected.value = true;
                    resolve(true);
                    return;
                }

                if (Date.now() - startTime >= timeout) {
                    clearInterval(checkConnection);
                    console.warn('[SocketManager] Connection timeout after', timeout, 'ms');
                    resolve(false);
                }
            }, checkInterval);
        });
    }

    /**
     * Emit với auto-retry và queue
     */
    function emitWithRetry(event, data, options = {}) {
        const {
            maxRetries = 3,
            retryDelay = 1000,
            queueIfDisconnected = true,
            callback = null
        } = options;

        return new Promise((resolve, reject) => {
            const attemptEmit = (attempt = 0) => {
                if (!isConnected.value || !socketInstance.value?.id) {
                    if (queueIfDisconnected) {
                        pendingActions.value.push({
                            event,
                            data,
                            callback,
                            resolve,
                            reject,
                            timestamp: Date.now()
                        });
                        return;
                    } else {
                        reject(new Error('Socket not connected'));
                        return;
                    }
                }

                try {
                    if (callback) {
                        socketInstance.value.emit(event, data, (response) => {
                            callback(response);
                            resolve(response);
                        });
                    } else {
                        socketInstance.value.emit(event, data);
                        resolve(true);
                    }
                } catch (error) {
                    console.error('[SocketManager] Emit error:', error);

                    if (attempt < maxRetries) {
                        setTimeout(() => attemptEmit(attempt + 1), retryDelay);
                    } else {
                        reject(error);
                    }
                }
            };

            attemptEmit();
        });
    }

    function processPendingActions() {
        if (!pendingActions.value.length) return;

        const actions = [...pendingActions.value];
        pendingActions.value = [];

        actions.forEach((action) => {
            if (Date.now() - action.timestamp > 30000) {
                action.reject?.(new Error('Action expired'));
                return;
            }

            emitWithRetry(action.event, action.data, {
                callback: action.callback,
                queueIfDisconnected: false
            })
                .then(action.resolve)
                .catch(action.reject);
        });
    }

    /**
     * Safe listener registration
     */
    function safeOn(event, handler) {
        if (!socketInstance.value) {
            console.warn('[SocketManager] Cannot register listener, socket not initialized:', event);
            return false;
        }

        socketInstance.value.off(event);
        socketInstance.value.on(event, handler);
        return true;
    }

    function safeOff(event) {
        if (!socketInstance.value) return;
        socketInstance.value.off(event);
    }

    function forceReconnect() {
        if (!socketInstance.value) return;

        socketInstance.value.disconnect();

        setTimeout(() => {
            socketInstance.value.connect();
        }, 500);
    }

    function cleanup() {
        if (!socketInstance.value) return;

        socketInstance.value.off('connect', handleConnect);
        socketInstance.value.off('disconnect', handleDisconnect);
        socketInstance.value.off('connect_error', handleConnectError);
        socketInstance.value.off('reconnect', handleReconnect);
        socketInstance.value.off('reconnect_attempt', handleReconnectAttempt);

        pendingActions.value = [];
        isInitialized.value = false;
    }

    return {
        socket: socketInstance,
        isConnected,
        isConnecting,
        isInitialized,
        connectionAttempts,
        pendingActionsCount: computed(() => pendingActions.value.length),

        initSocket,
        waitForConnection,
        emitWithRetry,
        processPendingActions,
        safeOn,
        safeOff,
        forceReconnect,
        cleanup,
        connectServer
    };
}